import { APIError } from "@certn/api";

import { OUTSTANDING_REQUEST_STATUS } from "./constants";
import {
  InformationRequest,
  InformationRequestItemAnswer,
  InformationRequestSummary,
} from "./types";
import { Schemas } from "../../types";

export function isItemSubmitted(item: { submitted_at: string | null }) {
  return item.submitted_at != null;
}

export function hasUnsubmittedItems(
  request: Pick<
    InformationRequestSummary,
    "item_count" | "submitted_item_count"
  >
) {
  return request.submitted_item_count < request.item_count;
}

export function isOutstandingRequest(
  request: Pick<
    InformationRequestSummary,
    "status" | "item_count" | "submitted_item_count"
  >
) {
  return (
    request.status === OUTSTANDING_REQUEST_STATUS &&
    hasUnsubmittedItems(request)
  );
}

/**
 * Requests are only sent after the applicant submits, and never on cancelled
 * cases or once every check is complete.
 */
export function canHaveOutstandingRequests(
  completeCase: Schemas["CompleteCaseList"]
) {
  return (
    completeCase.applicant_status === "APPLICANT_SUBMITTED" &&
    completeCase.overall_status !== "CANCELLED" &&
    completeCase.derived_checks_status !== "COMPLETE" &&
    !completeCase.archived_on
  );
}

export function countUnsubmittedItems(requests: InformationRequestSummary[]) {
  return requests.reduce(
    (total, request) =>
      total + (request.item_count - request.submitted_item_count),
    0
  );
}

export function getOldestSentAt(requests: InformationRequestSummary[]) {
  const sentDates = requests
    .map((request) => request.sent_at)
    .filter((sentAt): sentAt is string => Boolean(sentAt))
    .sort();
  return sentDates[0] ?? null;
}

export function getUnansweredItemIds(error: unknown): string[] {
  if (!(error instanceof APIError)) {
    return [];
  }
  const ids = error.body.unanswered_item_ids;
  return Array.isArray(ids)
    ? ids.filter((id): id is string => typeof id === "string")
    : [];
}

const SCROLL_WAIT_MS = 5000;

function findFirstItem(itemIds: string[]) {
  return Array.from(
    document.querySelectorAll<HTMLElement>("[data-item-id]")
  ).find((element) => itemIds.includes(element.dataset.itemId ?? ""));
}

function revealItem(item: HTMLElement) {
  item.scrollIntoView?.({ behavior: "smooth", block: "center" });
  item
    .querySelector<HTMLElement>(
      "textarea, input:not([type='file']):not([type='checkbox'])"
    )
    ?.focus({ preventScroll: true });
}

/** Waits briefly for the item to render, for requests that load after a refresh. */
export function scrollToFirstItem(itemIds: string[]) {
  const first = findFirstItem(itemIds);
  if (first) {
    revealItem(first);
    return;
  }
  const observer = new MutationObserver(() => {
    const rendered = findFirstItem(itemIds);
    if (rendered) {
      stop();
      revealItem(rendered);
    }
  });
  const timeout = window.setTimeout(
    () => observer.disconnect(),
    SCROLL_WAIT_MS
  );
  const stop = () => {
    observer.disconnect();
    window.clearTimeout(timeout);
  };
  observer.observe(document.body, { childList: true, subtree: true });
}

export function applyItemAnswer<Request extends InformationRequest>(
  request: Request | undefined,
  answer: InformationRequestItemAnswer
): Request | undefined {
  if (!request) {
    return request;
  }
  return {
    ...request,
    items: request.items.map((item) =>
      item.id === answer.id ? { ...item, ...answer } : item
    ),
  };
}
